import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Offer } from '../../model/offer';
import { Observable, map, shareReplay, forkJoin, catchError, of, ignoreElements, concat, endWith, tap } from 'rxjs';
import { DatePipe } from '@angular/common';
import { HttpService } from '../http.service';
import { AmenityDTO } from '../../model/amentyDTO';
import { GuestDTO } from '../../model/guestDTO';
import { StatusDTO } from '../../model/statusDTO';
import { ConfirmationDTO } from '../../model/confirmationDTO';
import { environment } from '../../../environments/environment';
import { OfferExpiresDTO } from '../../model/offerExpiresDTO';
import { AcceptedOfferDTO } from '../../model/acceptedOfferDTO';
import { UpgradeAvailabilityDTO } from '../../model/upgradeAvailabilityDTO';
import { SharedService } from '../shared.service';
import { ThemeService } from '../theme.service';
import { OfferCategory } from '../../model/offer-category.model';
import { OfferCategoryService } from '../offer-category.service';
import { OfferAliasAcceptedDTO } from '@app/model/offerAliasAcceptedDTO';
import { ExistingOffersetDTO } from '@app/model/exisitingOffersetDTO';
import { SelectedOfferDTO } from '@app/model/selectedOfferDTO';

// ...existing imports...

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private domain: string = '';
  private guestOfferDomain: string = '';
  private _url: string = '';
  datepipe: DatePipe = new DatePipe('en-US');


  constructor(
    @Inject(DOCUMENT) private _doc: Document,
    private http: HttpClient,
    private httpService: HttpService,
    private sharedService: SharedService,
    private themeService: ThemeService,
    private offerCategoryService: OfferCategoryService,
  ) {
    this.domain = environment.domain;
    this.guestOfferDomain = environment.guestOfferDomain;
    this._url = this.domain + 'guestoffers/';
  }


  extractUniqueTagsAndBedTypes(offers: Offer[]): { tags: string[], bedTypes: string[] } {
    const tagSet = new Set<string>();
    const bedTypeSet = new Set<string>();
    
    for (const offer of offers) {
      // Add non-empty bed types
      if (offer.upsell_room_bed_type?.trim()) {
        bedTypeSet.add(offer.upsell_room_bed_type.trim());
      }

      // Add all room tags
      if (Array.isArray(offer.upsell_room_tags)) {
        offer.upsell_room_tags.forEach(tag => {
          if (tag?.trim()) {
            tagSet.add(tag.trim());
          }
        });
      }
    }

    const sortedTagSet = Array.from(tagSet).sort((a, b) => a.localeCompare(b));
    const sortedBedTypeSet = Array.from(bedTypeSet).sort((a, b) => a.localeCompare(b));

    const tagSetOut = new Set<string>(['All', ...sortedTagSet]);
    const bedTypeSetOut = new Set<string>(['All', ...sortedBedTypeSet]);

    return {
      tags: Array.from(tagSetOut),
      bedTypes: Array.from(bedTypeSetOut)
    };
  }

  extractUniqueTagsAndBedTypesDict(offers: Offer[]): {
    tags: { [key: string]: { flg_enabled: boolean } },
    bedTypes: { [key: string]: { flg_enabled: boolean } }
  } {
    const tagSet = new Set<string>();
    const bedTypeSet = new Set<string>();

    tagSet.add('All');
    bedTypeSet.add('All');

    for (const offer of offers) {
      if (offer.upsell_room_bed_type?.trim()) {
        bedTypeSet.add(offer.upsell_room_bed_type.trim());
      }

      if (Array.isArray(offer.upsell_room_tags)) {
        for (const tag of offer.upsell_room_tags) {
          if (tag?.trim()) {
            tagSet.add(tag.trim());
          }
        }
      }
    }

    // Convert sets into objects with flg_enabled flags
    const tagsObj: { [key: string]: { flg_enabled: boolean } } = {};
    const bedTypesObj: { [key: string]: { flg_enabled: boolean } } = {};

    Array.from(tagSet).sort((a, b) => a.localeCompare(b)).forEach(tag => {
      tagsObj[tag] = { flg_enabled: tag === 'All' };
    });

    Array.from(bedTypeSet).sort((a, b) => a.localeCompare(b)).forEach(bed => {
      bedTypesObj[bed] = { flg_enabled: bed === 'All' };
    });

    return {
      tags: tagsObj,
      bedTypes: bedTypesObj
    };
  }

extractUniqueTagsAndBedTypesDictOfOfferIds(offers: Offer[]): {
  tags: { [key: string]: string[] },
  bedTypes: { [key: string]: string[] }
} {
  const tagsMap: { [key: string]: Set<string> } = { All: new Set<string>() };
  const bedTypesMap: { [key: string]: Set<string> } = { All: new Set<string>() };

  for (const offer of offers) {
    const offerId = offer.offer_id;

    // Add to 'All' by default
    bedTypesMap['All'].add(offerId);
    tagsMap['All'].add(offerId);

    // Bed type
    const bedType = offer.upsell_room_bed_type?.trim();
    if (bedType) {
      if (!bedTypesMap[bedType]) {
        bedTypesMap[bedType] = new Set<string>();
      }
      bedTypesMap[bedType].add(offerId);
    }

    // Tags
    if (Array.isArray(offer.upsell_room_tags)) {
      for (const tag of offer.upsell_room_tags) {
        const cleanTag = tag?.trim();
        if (cleanTag) {
          if (!tagsMap[cleanTag]) {
            tagsMap[cleanTag] = new Set<string>();
          }
          tagsMap[cleanTag].add(offerId);
        }
      }
    }
  }

  // Convert Sets to sorted arrays
  const sortedTagsMap: { [key: string]: string[] } = {};
  const sortedBedTypesMap: { [key: string]: string[] } = {};

  Object.keys(tagsMap)
    .sort((a, b) => a.localeCompare(b))
    .forEach(key => {
      sortedTagsMap[key] = Array.from(tagsMap[key]).sort();
    });

  Object.keys(bedTypesMap)
    .sort((a, b) => a.localeCompare(b))
    .forEach(key => {
      sortedBedTypesMap[key] = Array.from(bedTypesMap[key]).sort();
    });

  return {
    tags: sortedTagsMap,
    bedTypes: sortedBedTypesMap
  };
}


  
  /**
   * Fetches and processes all offer-related data in a single observable.
   * Returns an observable of the filtered offers (max 3), and updates sharedService and HomeComponent state as needed.
   */
 public getFullOfferData(
    offerAliasId: string,
    propId: string,
    component: any, // Pass HomeComponent instance for state updates
    offerCategory: OfferCategory = 'ROOM',
    flg_addon: boolean = false,
    offerSetId: string | null = null 
  ): Observable<Offer[]> {
    const guestInfo$ = this.getGuestInfo(offerAliasId, propId).pipe(shareReplay(1));
    const amenities$ = this.getAllOfferAmenties(offerAliasId, propId).pipe(shareReplay(1));
    let offerAlias$ = this.createOfferFromAlias(offerAliasId, propId, flg_addon, offerCategory).pipe(shareReplay(1));
    
    if (offerSetId && offerSetId !== '00000000-0000-0000-0000-000000000000') {
      offerAlias$ = this.createOfferFromAlias(offerAliasId, propId, flg_addon, offerCategory, offerSetId).pipe(shareReplay(1));
    } 

    const data: Observable<any>[] = [offerAlias$, amenities$];
    const offers$ = forkJoin(data).pipe(
      shareReplay(1),
      map(ofr => {
        if (ofr) {
          let offers = ofr[0] as Offer[];

          let ofr_filter = offers.filter(ofr => ofr.flg_valid_upsell === true);
          // Only flg_valid_upsell offers should be considered for display and further processing
          if (component) component.fullOffers = ofr_filter;
          let tags = [] as string[];
          let bedTypes = [] as string[];

          if (offerCategory !== 'ROOM') {
            ofr_filter = ofr_filter.filter((ofr: Offer) =>
              this.offerCategoryService.isOfferInCategory(ofr, offerCategory),
            );
          }
          
          if (component) component.offerCount = ofr_filter.length;
          let amenities = [] as AmenityDTO[];
          if (ofr[1]) {
            amenities = ofr[1] as AmenityDTO[];
          }
          ofr_filter.map(offer => {
            offer.offer_alias_id = offerAliasId;
            if (amenities) {
              let amenity = amenities.filter(a => a.room_category_id === offer.upsell_room_category_id)[0];
              offer.amenities = { ...amenity };              
            } else {
              offer.amenities = { ...({ "upsell_room_name": "Unknown" } as AmenityDTO) };
            }
          });
          if (ofr[2]) {
            if (component) component.upgradeAvailability = ofr[2] as UpgradeAvailabilityDTO;
            this.sharedService.setUpgradeAvailability(ofr[2] as UpgradeAvailabilityDTO);
          }

          return ofr_filter;
        }
        return [];
      }),
      map(offers => {
        // Keep offer-derived state in sync immediately (do not wait for guestInfo$).
        // Reapply session favorites (ROOM → ECI → ROOM should preserve favorites)
        this.sharedService.applyFavoritesToOffers(offerAliasId, offers);

        if (component) component.offers = offers;

        const { tags, bedTypes } = this.extractUniqueTagsAndBedTypes(offers);
        if (component) component.tags = tags;
        if (component) component.bedTypes = bedTypes;

        const { tags: tagsDict, bedTypes: bedTypesDict } = this.extractUniqueTagsAndBedTypesDict(offers);
        if (component) component.tagsDict = tagsDict;
        if (component) component.bedTypesDict = bedTypesDict;

        const { tags: tagsDictOfferIds, bedTypes: bedTypesDictOfferIds } =
          this.extractUniqueTagsAndBedTypesDictOfOfferIds(offers);
        if (component) component.tagsOfOfferIds = tagsDictOfferIds;
        if (component) component.bedTypesOfOfferIds = bedTypesDictOfferIds;

        this.sharedService.setSelectedOfferSet(offers);
        if (offers.length > 0) {
          this.sharedService.setOfferFilterOfferSetId(offerCategory, offers[0].offer_set_id);
          this.sharedService.addOfferSet(offers[0].offer_set_id, offers);
        }

        // Guest info can arrive independently; only map guest fields here.
        guestInfo$.subscribe(res => {
          const guest_info = res;
          this.sharedService.guestFirstName = guest_info.guest_fname;
          this.sharedService.guestLastName = guest_info.guest_lname;
          this.sharedService.confirmationNumber = guest_info.conf_num;
          this.sharedService.arrivalDate = guest_info.dt_arrival;
          this.sharedService.lengthOfStay = guest_info.los;
          this.sharedService.flg_light_mode_default = guest_info.flg_light_mode_default;
          this.sharedService.baseRoomCategoryName = guest_info.room_category_name;
          this.sharedService.propertyName = guest_info.prop_name;
          this.sharedService.applicationConfig = guest_info.application_config;
          this.sharedService.flg_tiles_view_default = guest_info.flg_tiles_view_default;
          this.onAutoSequenceChange(guest_info.application_config?.prestay?.flg_auto_sequencing ?? false);

          if (component?.updateViewModeFromGuest) {
            component.updateViewModeFromGuest(guest_info.flg_tiles_view_default ?? true);
          }

          if (component?.updateThemeModeFromGuest) {
            component.updateThemeModeFromGuest(guest_info.flg_light_mode_default ?? false);
          }

          // Apply guest-configured theme when present; otherwise use default theme.
          try {
            const desiredTheme = this.sharedService.applicationConfig?.prestay?.theme;
            this.themeService.setThemeOrFallback(desiredTheme, 'zen');
          } catch {
            // ignore
          }
        });

        return this.sharedService.filterDefaultAndValidOffers(offers);
      })
    );

    return offers$;

  }

  onAutoSequenceChange(autoSequenceEnabled: boolean): void {
    const autoSequence = autoSequenceEnabled ? 'on' : 'off';
    try {
      sessionStorage.setItem('autoSequence', autoSequence);
      this.sharedService.autoSequenceEnabled = autoSequenceEnabled;
    } catch {
      // ignore (some environments block storage writes)
    }
  }

  public resolveAliasFromLink(payload: {
    prop_id: string;
    key: string;
    conf_num?: string;
    dt_arrival?: string;
    los?: string;
    base_room_type_idn?: string;
    email?: string;
    ofr_origin?: string;
  }): Observable<string> {
    const url = this._url + 'alias/link/'+ payload.prop_id;
    const body = JSON.stringify(payload);

    return this.httpService.post(url, body).pipe(
      shareReplay(1),
      map(res => {
        console.debug('[OfferService] resolveAliasFromLink response', res);
        if (!res) {
          throw new Error('Alias link response is empty');
        }

        const aliasId = res;

        if (!aliasId || typeof aliasId !== 'string') {
          throw new Error('Alias link response did not include a valid alias_id');
        }

        return aliasId;
      }),
    );
  }

  public check_upgrade_availability(alias_id: string, prop_id: string, 
                                    check_room_availability: boolean = true,
                                    check_eci_availability: boolean = true,
                                    check_lco_availability: boolean = true,
                                    check_addon_availability: boolean = true
                                    ): Observable<UpgradeAvailabilityDTO> {

    const url = this._url + 'checkupgradeavailability/' + alias_id + '/' + prop_id;
    const body = JSON.stringify({
      'check_addon_availability': check_addon_availability,
      'check_room_availability': check_room_availability,
      'check_eci_availability': check_eci_availability,
      'check_lco_availability': check_lco_availability
    });

    return this.httpService.post(url, body).pipe(
      shareReplay(1),
      map(res => {
        const upgradeAvailability: UpgradeAvailabilityDTO = { ...res['data'] };        
        return upgradeAvailability;
      })
    );
  }

  public getAllOfferAmenties(offer_alias_id:string, prop_id: string): Observable<AmenityDTO[]> {
    const url = this._url + 'amenities/' + offer_alias_id + '/' + prop_id
    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {

        let amenties = res;
        let result: AmenityDTO[] = [];

        for(const a of amenties){
          result.push({ ...a, icons: [...a.icons]});
        }
        return result;
      }) 
    );
  }

  public getGuestInfo(offer_alias_id:string, prop_id: string): Observable<GuestDTO> {
    const url = this._url + 'guest/' + offer_alias_id + '/' + prop_id
    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: GuestDTO = {...res}
        return result;
      }) 
    );
  }

  public getOfferStatus(offer_alias_id:string, prop_id: string): Observable<StatusDTO> {
    const url = this._url + 'offerStatus/' + offer_alias_id + '/' + prop_id
    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: StatusDTO = {...res}[0]
        return result;
      }) 
    );
  }

  public getConfrimationInfo(offer_alias_id: string, prop_id: string): Observable<ConfirmationDTO[]> {
    
    const url = this._url + 'offerConfirmation/' + offer_alias_id + '/' + prop_id
    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: ConfirmationDTO[] = [...res]
        return result;
      }) 
    );
  }
  
  public getOfferExpires(offer_alias_id:string, prop_id: string, offer_set_id: string = '00000000-0000-0000-0000-000000000000'): Observable<OfferExpiresDTO> {
    
    const url = this._url + 'getOfferExpires/' + offer_alias_id + '/' + prop_id

    let bodyObj: any = {
      'offer_set_id': offer_set_id
    };
    
    let body = JSON.stringify(bodyObj);

    return this.httpService.put(url, body).pipe(
      shareReplay(1),
      map(res => {
        let result: OfferExpiresDTO = {...res}
        return result;
      }) 
    );
  }

  public getAcceptedOffers(offer_alias_id:string, prop_id: string): Observable<AcceptedOfferDTO[]> {
    
    const url = this._url + 'acceptedoffers/' + offer_alias_id + '/' + prop_id

    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: AcceptedOfferDTO[] = {...res}
        return result;
      }) 
    );
  }

  public setOfferDeclined(
    prop_id: string, 
    offer_alias_id: string, 
    reason_id: number | undefined = undefined
    ) {
    const url = this._url +  'decline/' + offer_alias_id + '/' + prop_id;

    let parameters: any = {'offer_alias_id': offer_alias_id};
    if (reason_id) parameters['reasonId'] = reason_id;
    const body = JSON.stringify(parameters);
    return this.httpService.put(url, body);
  }

  public setOfferAccepted(prop_id: string, offer_alias_id: string, offer_id: string) {
    
    const url = this._url + 'accept/' + offer_alias_id + '/' + prop_id;
    const body = JSON.stringify({
        'offer_id': offer_id,
        'environment_name': environment.environmentName
      })

    return this.httpService.put(url, body);
  }

  public requestUpgrades(offer_alias_id: string, prop_id: string, 
                         requestedOfferSetIds: string[], requestedOfferIds: string[]): Observable<any>
  {
    const url = this._url  + 'requestUpgrades/' + offer_alias_id + '/' + prop_id;

    const body = 
      JSON.stringify({
         'requested_offer_set_ids' : requestedOfferSetIds,
         'requested_offer_ids' : requestedOfferIds
        });

    return this.httpService
      .put(
        url,
        body
      ).pipe(shareReplay(1));
  }

  public confirmOfferAccepted(prop_id: string, offer_alias_id: string, 
                              offer_set_ids: string[], offer_ids: string[],
                              token: string): Observable<any> {
    
    const url = this._url + 'confirmOfferAccept/' + offer_alias_id + '/' + prop_id;

    const body = 
      JSON.stringify({
        'token': token,
        'offer_set_ids': offer_set_ids,
        'offer_ids': offer_ids        
      });

    return this.httpService.put(url, body).pipe(
      shareReplay(1),
      map(res => {
        let result: ConfirmationDTO[] = [...res]
        return result;
      }) 
    );
  }

  public setGuestEmail(offer_alias_id: string, prop_id: string, email:string) {    
    const url = this._url + 'updateGuestEmail/' + offer_alias_id + '/' + prop_id;
    const body = JSON.stringify({ 'email': email });  
    return this.httpService.put(url, body);
  }

  public emailOfferUpgradeAcknowledgement(prop_id: string, offer_alias_id: string,  
                                          offer_set_ids: string[], offer_ids: string[]): Observable<any> { 

    const url = this._url + 'emailGuestOfferUpgradeAcknowledgement/' + prop_id + '/' + offer_alias_id    
    const body = JSON.stringify({
      'offer_set_ids': offer_set_ids,
      'offer_ids': offer_ids,
      'offer_environment_name': environment.environmentName,
    });

    return this.http.post(url, body).pipe(
      shareReplay(1),
      map(res => {
        return res;
      }) 
    );
  }

  public createOfferFromAlias(alias_id: string, prop_id: string, 
                              flg_addon: boolean=false, offer_category: OfferCategory = 'ROOM', 
                              offer_set_id: string | null = null)
                              : Observable<any> {
    
    const url = this._url + 'offer/' + alias_id + '/' + prop_id;
    
    let bodyObj: any = {
      'flg_addon': flg_addon,
      'ofr_origin': 'DE',
      'ofr_category': offer_category
    };
    
    if (offer_set_id) { 
      bodyObj.offer_set_id = offer_set_id;
    }

    let body = JSON.stringify(bodyObj);

    return this.httpService.post(url, body).pipe(
      shareReplay(1),
      map(res => {

        let offer = res['data'];
        let offers = offer['offers'];
        let result: Offer[] = [];
        const failOnUnknownCategory = !environment.production;
 
        if(res && res['data'] && offers && offers.length > 0){
          for(let ofr of offers){
            const parsedCategory = this.offerCategoryService.parseApiCategory(ofr?.ofr_category, {
              failOnUnknown: failOnUnknownCategory,
              context: `offer_id:${ofr?.offer_id ?? 'unknown'}`,
            });

            if (!parsedCategory) {
              continue;
            }

            result.push({
              ...ofr,
              ofr_category: parsedCategory,
              offer_set_id: offer['offer_set_id'],              
              tenant_id: offer['tenant_id'],
              prop_id: offer['prop_id'],                  
              conf_num: offer['conf_num'],
              ofr_origin: offer['ofr_origin'], 
              los: offer['los'],              
              base_room_category_id: offer['base_room_category_id'], 
              dt_arrival: offer['dt_arrival'],
              confirmation_type: 'E',
              prop_info: 'Property Info Placeholder',

            } as Offer)
          }
        }
        return result
      }) 
    );
  }

  public checkOfferAliasAccepted(offerAliasId: string, propId: string): Observable<OfferAliasAcceptedDTO> {
    
    const url = this._url + 'checkOfferAliasAccepted/' + offerAliasId + '/' + propId

    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: OfferAliasAcceptedDTO = { 
            offerError: res.offer_error ?? true,
            accepted: res.accepted ?? false,
            acknowledgementEmailSent: res.acknowledgement_email_sent ?? false,
            requestedOfferSetIds: res.requested_offer_set_ids ?? [],
            requestedOfferIds: res.requested_offer_ids ?? []
         }
        return result;
      }),
      catchError(err => {
            let result: OfferAliasAcceptedDTO = { 
            offerError: true,
            accepted: false,
            acknowledgementEmailSent: false,
            requestedOfferSetIds: [],
            requestedOfferIds: []
          }
        return of(result);
      } 
    )
  )}


  public getExistingOfferset(offerAliasId: string, propId: string, ofr_category: string): Observable<ExistingOffersetDTO> {
    
    const url = this._url + 'getExistingOfferset/' + offerAliasId + '/' + propId + '/' + ofr_category

    return this.httpService.get(url).pipe(
      shareReplay(1),
      map(res => {
        let result: ExistingOffersetDTO = res ?? {};
        return result;
      }),
      catchError(err => {
        let result: ExistingOffersetDTO = { ofr_category: '', ts_generated: '', offer_set_id: '' };
        return of(result);
      } 
    )
  )}

  // Tagged for future development: Intended to allow for cross device retrieval of selected offers
  // public setSelectedOffer(offerAliasId: string, propId: string, offer_set_id: string, offer_id: string): Observable<any> {
    
  //   const url = this._url + 'setSelectedOffer/' + offerAliasId + '/' + propId

  //   const body = JSON.stringify({
  //       'offer_set_id': offer_set_id,
  //       'offer_id': offer_id
  //     })

  //   return this.httpService.put(url, body);

  // }

  // public getSelectedOffers(offerAliasId: string, propId: string): Observable<SelectedOfferDTO[]> {
    
  //   const url = this._url + 'getSelectedOffers/' + offerAliasId + '/' + propId

  //   return this.httpService.get(url).pipe(
  //     shareReplay(1),
  //     map(res => {
  //       let result: SelectedOfferDTO[] = res ?? [];
  //       return result;
  //     }),
  //     catchError(err => {
  //           let result: SelectedOfferDTO[] = [] as SelectedOfferDTO[];
  //       return of(result);
  //       } 
  //     )
  //   )
  // }

}